import { getLegalEntityLabel, type LegalEntity } from './legal-entity';

export interface ReconciliationOperationForEmail {
  date: string;
  description: string;
  debit: number;
  credit: number;
  invoiceId?: string;
  invoiceNumber?: string;
  invoiceDate?: string;
  actId?: string;
  actDate?: string;
  actReturnStatus?: string;
  isPending?: boolean;
}

export interface ReconciliationDataForEmail {
  client?: {
    byActs?: boolean;
  };
  operations: ReconciliationOperationForEmail[];
  openingBalance: { debit: number; credit: number };
}

export interface UnpaidDocumentForEmail {
  invoiceId: string;
  actId?: string;
  invoiceNumber: string;
  date: string;
  invoiceDate?: string;
  actDate?: string;
  amount: number;
  remainingAmount: number;
  isPending: boolean;
  actReturnStatus?: string;
}

export type UnsurrenderedDocumentTypeForEmail = 'CONTRACT' | 'AMENDMENT' | 'ACT';

export interface UnsurrenderedDocumentForEmail {
  id: string;
  type: UnsurrenderedDocumentTypeForEmail;
  displayNumber: string;
  date: string;
  barcode?: string;
  returnStatus?: string;
  paymentState?: 'PAID' | 'UNPAID';
}

export interface EmailDraft {
  subject: string;
  body: string;
}

export function formatEmailMoney(amount: number): string {
  return amount.toLocaleString('ru-RU', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

export function formatEmailDateShort(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString('ru-RU');
}

export function formatEmailDateFull(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString('ru-RU', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
}

export function formatEmailPeriod(from: string, to: string): string {
  const fromDate = new Date(from);
  const toDate = new Date(to);

  const fromYear = fromDate.getFullYear();
  const toYear = toDate.getFullYear();

  const fromMonth = fromDate.toLocaleDateString('ru-RU', { month: 'long', year: 'numeric' });
  const toMonth = toDate.toLocaleDateString('ru-RU', { month: 'long', year: 'numeric' });

  if (fromMonth === toMonth) return fromMonth;
  if (fromYear !== toYear) return `${fromMonth} - ${toMonth}`;

  const fromMonthOnly = fromDate.toLocaleDateString('ru-RU', { month: 'long' });
  return `${fromMonthOnly} - ${toMonth}`;
}

export function getUnsurrenderedDocTypeLabel(type: UnsurrenderedDocumentTypeForEmail): string {
  switch (type) {
    case 'CONTRACT':
      return 'Договор';
    case 'AMENDMENT':
      return 'Доп. соглашение';
    case 'ACT':
      return 'Акт';
  }
}

export function buildDebtEmailDraft(input: {
  clientLegalName: string;
  companyName: string;
  unpaidDocuments: UnpaidDocumentForEmail[];
  totalDebt: number;
}): EmailDraft {
  const documentsList = input.unpaidDocuments
    .map((doc) => {
      const docType = doc.isPending ? 'Счёт' : 'Акт';
      return `  - ${docType} №${doc.invoiceNumber} от ${formatEmailDateFull(doc.date)} на сумму ${formatEmailMoney(doc.remainingAmount)} BYN`;
    })
    .join('\n');

  return {
    subject: `Задолженность ${input.clientLegalName} перед ${input.companyName}`,
    body: `Добрый день!

Напоминаем о задолженности ${input.clientLegalName} перед ${input.companyName} на общую сумму ${formatEmailMoney(input.totalDebt)} BYN.

Неоплаченные документы:
${documentsList}

Просим произвести оплату в ближайшее время.

Документы во вложении.

С уважением,
${input.companyName}`,
  };
}

export function buildReconciliationEmailDraft(input: {
  legalEntity: LegalEntity;
  dateFrom: string;
  dateTo: string;
}): EmailDraft {
  const companyName = getLegalEntityLabel(input.legalEntity, 'official');
  return {
    subject: `Акт сверки ${companyName} за ${formatEmailPeriod(input.dateFrom, input.dateTo)}`,
    body: `Добрый день!

Во вложении акт сверки за период с ${formatEmailDateShort(input.dateFrom)} по ${formatEmailDateShort(input.dateTo)}.

С уважением,
${companyName}`,
  };
}

export function buildUnsurrenderedEmailDraft(input: {
  clientLegalName: string;
  companyName: string;
  documents: UnsurrenderedDocumentForEmail[];
}): EmailDraft {
  const documentsList = input.documents
    .map((doc) => `  - ${getUnsurrenderedDocTypeLabel(doc.type)} №${doc.displayNumber} от ${formatEmailDateFull(doc.date)}`)
    .join('\n');

  return {
    subject: `Возврат подписанных документов - ${input.clientLegalName}`,
    body: `Добрый день!

Напоминаем ${input.clientLegalName} о необходимости возврата подписанных документов в адрес ${input.companyName}.

Документы ожидающие возврата:
${documentsList}

Просим вернуть подписанные документы в ближайшее время.

Документы во вложении.

С уважением,
${input.companyName}`,
  };
}

function moneyRound(value: number): number {
  return Math.round(value * 100) / 100;
}

function toLocalDateKey(value: Date | string): string {
  const date = typeof value === 'string' ? new Date(value) : value;
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function isDateReached(date: string | undefined, asOfDate: Date | string): boolean {
  if (!date) return false;
  return toLocalDateKey(date) <= toLocalDateKey(asOfDate);
}

interface DocumentDebtCandidate {
  invoiceId: string;
  invoiceNumber: string;
  invoiceDate?: string;
  actId?: string;
  actDate?: string;
  actReturnStatus?: string;
  amount: number;
  orderDate: string;
  orderIndex: number;
}

function getCandidateDate(candidate: DocumentDebtCandidate, isPending: boolean): string {
  if (isPending) {
    return candidate.invoiceDate || candidate.orderDate;
  }
  return candidate.actDate || candidate.orderDate;
}

function selectDocumentForDebt(
  candidate: DocumentDebtCandidate,
  byActs: boolean,
  asOfDate: Date | string,
): UnpaidDocumentForEmail | null {
  if (byActs) {
    if (!candidate.actId) return null;
    return {
      invoiceId: candidate.invoiceId,
      actId: candidate.actId,
      invoiceNumber: candidate.invoiceNumber,
      date: getCandidateDate(candidate, false),
      invoiceDate: candidate.invoiceDate,
      actDate: candidate.actDate,
      amount: candidate.amount,
      remainingAmount: candidate.amount,
      isPending: false,
      actReturnStatus: candidate.actReturnStatus,
    };
  }

  const shouldSendAct = !!candidate.actId && isDateReached(candidate.actDate, asOfDate);
  return {
    invoiceId: candidate.invoiceId,
    actId: shouldSendAct ? candidate.actId : undefined,
    invoiceNumber: candidate.invoiceNumber,
    date: getCandidateDate(candidate, !shouldSendAct),
    invoiceDate: candidate.invoiceDate,
    actDate: candidate.actDate,
    amount: candidate.amount,
    remainingAmount: candidate.amount,
    isPending: !shouldSendAct,
    actReturnStatus: shouldSendAct ? candidate.actReturnStatus : undefined,
  };
}

export function computeUnpaidDocumentsForEmail(
  data: ReconciliationDataForEmail,
  options: { asOfDate?: Date | string } = {},
): { unpaidDocuments: UnpaidDocumentForEmail[]; totalDebt: number } {
  const openingDebtAmount = data.openingBalance?.debit || 0;
  const openingOverpayAmount = data.openingBalance?.credit || 0;
  const candidatesByInvoiceId = new Map<string, DocumentDebtCandidate>();
  data.operations.forEach((op, orderIndex) => {
    if (op.debit <= 0 || !op.invoiceId || !op.invoiceNumber) return;

    const existing = candidatesByInvoiceId.get(op.invoiceId);
    const opInvoiceDate = op.invoiceDate || (op.isPending ? op.date : undefined);
    const opActDate = op.actDate || (!op.isPending && op.actId ? op.date : undefined);
    const orderDate = opInvoiceDate || opActDate || op.date;

    if (!existing) {
      candidatesByInvoiceId.set(op.invoiceId, {
        invoiceId: op.invoiceId,
        invoiceNumber: op.invoiceNumber,
        invoiceDate: opInvoiceDate,
        actId: op.actId,
        actDate: opActDate,
        actReturnStatus: op.actReturnStatus,
        amount: op.debit,
        orderDate,
        orderIndex,
      });
      return;
    }

    if (!existing.invoiceDate && opInvoiceDate) existing.invoiceDate = opInvoiceDate;
    if (op.actId) existing.actId = op.actId;
    if (opActDate) existing.actDate = opActDate;
    if (op.actReturnStatus) existing.actReturnStatus = op.actReturnStatus;
    existing.amount = Math.max(existing.amount, op.debit);
    if (toLocalDateKey(orderDate) < toLocalDateKey(existing.orderDate)) {
      existing.orderDate = orderDate;
      existing.orderIndex = orderIndex;
    }
  });
  const invoiceOps = Array.from(candidatesByInvoiceId.values())
    .sort((a, b) => {
      const dateCompare = toLocalDateKey(a.orderDate).localeCompare(toLocalDateKey(b.orderDate));
      return dateCompare || a.orderIndex - b.orderIndex;
    })
    .map((candidate) => ({
      candidate,
      selected: selectDocumentForDebt(
        candidate,
        data.client?.byActs === true,
        options.asOfDate || new Date(),
      ),
    }))
    .filter((item): item is { candidate: DocumentDebtCandidate; selected: UnpaidDocumentForEmail } => item.selected !== null);
  const paymentOps = data.operations.filter((op) => op.credit > 0);

  let availablePayment = openingOverpayAmount;
  let openingDebtRemaining = openingDebtAmount;
  if (openingDebtAmount > 0 && availablePayment > 0) {
    const toPay = Math.min(availablePayment, openingDebtAmount);
    openingDebtRemaining -= toPay;
    availablePayment -= toPay;
  }

  const remainingPayments = paymentOps.map((op) => ({ ...op, remaining: op.credit }));

  for (const payment of remainingPayments) {
    if (openingDebtRemaining <= 0) break;
    if (payment.remaining <= 0) continue;
    const toPay = Math.min(payment.remaining, openingDebtRemaining);
    openingDebtRemaining -= toPay;
    payment.remaining -= toPay;
  }

  const unpaidDocuments = invoiceOps
    .map(({ candidate, selected }) => {
      let paidAmount = 0;
      const invoiceAmount = candidate.amount;

      if (availablePayment > 0) {
        const toPay = Math.min(availablePayment, invoiceAmount);
        paidAmount += toPay;
        availablePayment -= toPay;
      }

      for (const payment of remainingPayments) {
        if (payment.remaining <= 0) continue;
        if (paidAmount >= invoiceAmount) break;
        const toPay = Math.min(payment.remaining, invoiceAmount - paidAmount);
        paidAmount += toPay;
        payment.remaining -= toPay;
      }

      return {
        ...selected,
        amount: invoiceAmount,
        remainingAmount: moneyRound(invoiceAmount - paidAmount),
      };
    })
    .filter((doc) => doc.remainingAmount > 0);

  return {
    unpaidDocuments,
    totalDebt: moneyRound(unpaidDocuments.reduce((sum, doc) => sum + doc.remainingAmount, 0)),
  };
}
